import React, { useCallback, useEffect, useState } from 'react';
import { NotificationType } from '../../types';

interface NotificationProps {
  message: string;
  type: NotificationType;
  count?: number;
  onClose: () => void;
}

export const Notification: React.FC<NotificationProps> = ({ message, type, count = 1, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    // Allow animation to finish before calling parent's onClose
    setTimeout(onClose, 300);
  }, [onClose]);

  useEffect(() => {
    // Animate in
    setIsVisible(true);
    
    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [handleClose, count, message, type]);
  
  const baseClasses = 'pointer-events-auto relative w-full overflow-hidden rounded-2xl border p-4 pr-12 shadow-lg transition-all duration-300 ease-in-out transform backdrop-blur';
  
  const typeClasses = {
    success: 'border-green-400/40 bg-green-500 text-white dark:border-green-300/20 dark:bg-emerald-500/95',
    error: 'border-red-400/40 bg-red-500 text-white dark:border-red-300/20 dark:bg-red-500/95',
    info: 'border-blue-400/40 bg-blue-500 text-white dark:border-sky-300/20 dark:bg-sky-500/95',
  };

  const animationClasses = isVisible 
    ? 'opacity-100 translate-x-0'
    : 'opacity-0 translate-x-full';

  return (
    <div className={`${baseClasses} ${typeClasses[type]} ${animationClasses}`}>
      <div className="flex items-start gap-3">
        <p className="min-w-0 flex-1 text-sm font-medium leading-6 sm:text-base">{message}</p>
        {count > 1 ? (
          <span className="shrink-0 rounded-full bg-white/20 px-2 py-1 text-[11px] font-black uppercase tracking-wider text-white">
            x{count}
          </span>
        ) : null}
      </div>
      <button 
        onClick={handleClose} 
        className="absolute top-1/2 right-3 -translate-y-1/2 text-white/70 hover:text-white"
        aria-label="Close notification"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};
